import type {
  CreateSavedSearchInput,
  SavedSearchFilters,
  SavedSearchListResult,
  SavedSearchSummary,
  UpdateSavedSearchInput,
} from '@tabbio/types';
import { api } from './api';
import { getLibraryResponseError } from './library-api-error';

const DUPLICATE_NAME_SUFFIX_LIMIT = 5;

function isDuplicateNameError(error: unknown): boolean {
  return (
    error instanceof Error &&
    /already exists/i.test(error.message)
  );
}

export function buildAutoSavedSearchName(filters: SavedSearchFilters): string {
  const parts: string[] = [];
  if (filters.q?.trim()) {
    parts.push(filters.q.trim());
  }
  if (filters.locations?.length) {
    parts.push(filters.locations[0]);
  }
  if (filters.skills?.length) {
    parts.push(filters.skills[0]);
  }
  if (filters.companies?.length) {
    parts.push(filters.companies[0]);
  }
  const joined = parts.join(' · ').slice(0, 110);
  return joined || 'Saved search';
}

export async function fetchSavedSearches(): Promise<SavedSearchListResult> {
  const response = await api.get<SavedSearchListResult>('/api/library/saved-searches');
  const error = getLibraryResponseError(response);
  if (error) throw error;

  return response.data ?? { savedSearches: [], total: 0 };
}

export async function createSavedSearch(
  input: CreateSavedSearchInput,
): Promise<SavedSearchSummary> {
  // Retry with `(2)`, `(3)`, ... when the chosen name collides with an
  // existing saved search for this recruiter. The unique constraint is
  // (companyId, userId, name); auto-generated names will frequently repeat.
  for (let attempt = 0; attempt < DUPLICATE_NAME_SUFFIX_LIMIT; attempt++) {
    const name = attempt === 0 ? input.name : `${input.name} (${attempt + 1})`;
    try {
      const response = await api.post<SavedSearchSummary>(
        '/api/library/saved-searches',
        { ...input, name },
      );
      const error = getLibraryResponseError(response);
      if (error) throw error;
      if (!response.data) throw new Error('Saved search creation returned no data');
      return response.data;
    } catch (error) {
      if (
        attempt < DUPLICATE_NAME_SUFFIX_LIMIT - 1 &&
        isDuplicateNameError(error)
      ) {
        continue;
      }
      throw error;
    }
  }
  throw new Error('Could not save this search: too many duplicates');
}

export async function updateSavedSearch(
  savedSearchId: string,
  input: UpdateSavedSearchInput,
): Promise<SavedSearchSummary> {
  const response = await api.patch<SavedSearchSummary>(
    `/api/library/saved-searches/${savedSearchId}`,
    input,
  );
  const error = getLibraryResponseError(response);
  if (error) throw error;
  if (!response.data) throw new Error('Saved search update returned no data');
  return response.data;
}

export async function deleteSavedSearch(savedSearchId: string): Promise<void> {
  const response = await api.delete(`/api/library/saved-searches/${savedSearchId}`);
  const error = getLibraryResponseError(response);
  if (error) throw error;
}
